import { useEffect, useRef, useState } from "react";
import useOrbit from "../../../../hooks/useOrbit";
import useUrlParams from "../../../../hooks/useUrlParams";
import * as Cesium from "cesium";
import { getGroundStationPacket, getSatellitePackete, groundStationToSatelliteConnection } from "../../../../hooks/cesiumUtils";
import CustomLoader from "../../../../custom_components/CustomLoader";
import { get_promoted_sat_orbit } from "../../../../../Missions/Operate/utils";
import useOrbitParams from "../../../../hooks/useOrbitParams";
import { getCesiumViewerProps, getMapStyleId } from "../../../../../../utils/cesiumUtil";
import './Map.scss';
import useViewer from "../../../../hooks/useViewer";
import { createNaturalEarthProvider, offline } from "../../../../../../utils/commonFunctions";
import { ContactStation } from "../../../../types";
import useScenario from "../../../../hooks/useScenario";
import { isOperateMode } from "../../../../hooks/utils";
import { SCENARIO_DEPLOYMENT_TYPES } from "../../../../../simulate/constants";
import { getEnvVar } from "../../../../../../envUtils";

interface MapProps {
    selected_contact: {
        contact_type: string;
        contact_data: ContactStation,
    } | any | null
}

const Map = ({ selected_contact }: MapProps) => {
    const { orbitData } = useOrbit()
    const { update_viewer } = useViewer()
    const { launchDetails } = useOrbitParams()
    const { scenario_id, dashboard, sat_grp_id, sat_id } = useUrlParams()
    const { scenario } = useScenario()
    const operateMode = isOperateMode(dashboard, scenario)
    const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
    const isTrueTwinCommandCenter = deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER || deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE
    const [loading, setLoading] = useState<boolean>(true)
    const cesiumContainerRef = useRef<HTMLDivElement | null>(null)
    const [map_viewer, set_map_viewer] = useState<Cesium.Viewer | null>(null)

    const effectiveScenarioId = scenario_id || (scenario as any)?.id || (scenario as any)?.scenarioId || "default";

    const update_base_map_layer = (viewer: Cesium.Viewer, baseMapOption: string) => {
        if (!viewer || viewer.isDestroyed()) return;
        const mapboxToken = getEnvVar('REACT_APP_MAPBOX_TOKEN');
        const imaginary = new Cesium.MapboxStyleImageryProvider({
            styleId: getMapStyleId(baseMapOption),
            accessToken: mapboxToken
        });
        viewer.imageryLayers.addImageryProvider(offline() ? createNaturalEarthProvider() : imaginary);
    }

    useEffect(() => {
        let cesium_viewer: Cesium.Viewer | null = null;
        if (cesiumContainerRef.current && !map_viewer) {
            try {
                const mapboxToken = getEnvVar('REACT_APP_MAPBOX_TOKEN');
                const Imaginary = new Cesium.MapboxStyleImageryProvider({
                    styleId: 'dark-v10',
                    accessToken: mapboxToken
                });
                cesium_viewer = new Cesium.Viewer(cesiumContainerRef.current, {
                    ...getCesiumViewerProps(),
                    imageryProvider: offline() ? createNaturalEarthProvider() : Imaginary,
                });
                const map_details = JSON.parse(sessionStorage.getItem('map_details') || '{}');

                if (map_details[effectiveScenarioId]) {
                    if (!map_details?.[effectiveScenarioId]?.["Eclipse"] && cesium_viewer.scene.globe.enableLighting) {
                        cesium_viewer.scene.globe.enableLighting = !cesium_viewer.scene.globe.enableLighting
                    }
                    cesium_viewer.scene.morphTo2D(2);
                    update_base_map_layer(cesium_viewer, map_details?.[effectiveScenarioId]?.["Base Map"]);
                } else {
                    cesium_viewer.scene.morphTo2D(2)
                    cesium_viewer.imageryLayers.addImageryProvider(offline() ? createNaturalEarthProvider() : Imaginary);
                    const details = {
                        ...map_details,
                        [effectiveScenarioId]: {
                            "View": '2D',
                            "Base Map": 'Mapbox Dark',
                            "Eclipse": 'Eclipse',
                        }
                    }
                    sessionStorage.setItem('map_details', JSON.stringify(details));
                }
                cesium_viewer.camera.setView({
                    destination: Cesium.Cartesian3.fromDegrees(0, 0, 77000000)
                });
                cesium_viewer.scene.globe.showGroundAtmosphere = false;
                cesium_viewer.clock.shouldAnimate = true;
                cesium_viewer.clock.clockRange = Cesium.ClockRange.LOOP_STOP;
                cesium_viewer.scene.screenSpaceCameraController.enableRotate = true;
                cesium_viewer.scene.screenSpaceCameraController.enableTranslate = true;
                cesium_viewer.scene.screenSpaceCameraController.enableZoom = true;
                cesium_viewer.scene.screenSpaceCameraController.enableTilt = true;
                cesium_viewer.scene.screenSpaceCameraController.enableLook = true;
                cesium_viewer.scene.highDynamicRange = false;
                if (cesium_viewer.scene.skyAtmosphere) {
                    cesium_viewer.scene.skyAtmosphere.show = false;
                }

                const controller = cesium_viewer.scene.screenSpaceCameraController;
                controller.enableZoom = false; // Disables all zoom-related actions
                controller.enableLook = false; // Prevents right-click & drag to zoom
                controller.enableTilt = false; // Disables tilt zooming
                controller.enableCollisionDetection = false; // Optional: Disables zoom collision
                // Ensure multiplier value we set persists after initialization
                cesium_viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

                set_map_viewer(cesium_viewer);
                setLoading(false);
            } catch (err) {
                console.error("Cesium Viewer initialization error:", err);
                setLoading(false);
            }
        }

        return () => {
            if (cesium_viewer && !cesium_viewer.isDestroyed()) {
                try {
                    cesium_viewer.destroy();
                } catch (e) {
                    console.error("Cesium Viewer destroy error:", e);
                }
                set_map_viewer(null);
            }
        };
    }, [effectiveScenarioId]);

    useEffect(() => {
        if (!map_viewer || map_viewer.isDestroyed()) return;
        if (orbitData && (!operateMode || isTrueTwinCommandCenter)) {
            const selectedGroup = orbitData?.satellites?.find((satGroup: any) => satGroup.satelliteGroupId === sat_grp_id);
            const selectedSatellite = selectedGroup?.satelliteGroups?.find(
                sat => sat.satelliteId === sat_id
            );
            const document = selectedSatellite?.visualizationData?.documentMetadata
            const satellites = selectedSatellite?.visualizationData?.['satellites'] ? selectedSatellite?.visualizationData?.['satellites'] : []
            const groundStations = selectedSatellite?.visualizationData?.['groundStation'] ? selectedSatellite?.visualizationData?.['groundStation'] : []
            const satToGsConnection = selectedSatellite?.visualizationData?.['satToGsConnection'] ? selectedSatellite?.visualizationData?.['satToGsConnection'] : []
            createPackets(document, satellites, groundStations, satToGsConnection)
        } else if (operateMode && !isTrueTwinCommandCenter && launchDetails?.length > 0) {
            get_promoted_sat_orbit(map_viewer, launchDetails)
        }
    }, [orbitData, map_viewer, dashboard, launchDetails, sat_id, sat_grp_id, operateMode, scenario, isTrueTwinCommandCenter])

    const createPackets = (document: any, satellites: any, groundStations: any, satToGsConnection: any) => {
        if (!map_viewer || map_viewer.isDestroyed() || !map_viewer.dataSources) return;
        try {
            // clean the previous czml or data source
            map_viewer.dataSources.removeAll();
            const czmlPackages: any[] = []
            let satelliteczml: any[] = [];
            let groundStationczml: any[] = [];
            let satToGsConnections: any[] = [];

            satelliteczml = getSatellitePackete(document, satellites, sat_id)  // Create the satellite packet
            groundStationczml = getGroundStationPacket(satellites, groundStations, true)  // Create the Ground station packet
            satToGsConnections = groundStationToSatelliteConnection(satToGsConnection)

            if (satelliteczml?.length > 0) {
                czmlPackages.push(...satelliteczml)
            } if (groundStationczml?.length > 0) {
                czmlPackages.push(...groundStationczml)
            } if (satToGsConnections?.length > 0) {
                czmlPackages.push(...satToGsConnections)
            }

            if (czmlPackages.length > 0) {
                map_viewer.dataSources.add(Cesium.CzmlDataSource.load(czmlPackages));
                update_viewer(map_viewer)
                map_viewer.scene.requestRender();
            }
        } catch (e) {
            console.error("Error creating packets in Map:", e);
        }
    }

    useEffect(() => {
        if (selected_contact?.contact_data && map_viewer && !map_viewer.isDestroyed()) {
            const gsProps = selected_contact?.contact_data?.['groundStationProperties'];
            if (gsProps && gsProps.latitude !== undefined && gsProps.longitude !== undefined) {
                const lat = parseFloat(gsProps.latitude);
                const lon = parseFloat(gsProps.longitude);
                if (!isNaN(lat) && !isNaN(lon)) {
                    try {
                        map_viewer.camera.setView({
                            destination: Cesium.Cartesian3.fromDegrees(lon, lat, 5000000),
                            orientation: {
                                heading: Cesium.Math.toRadians(0.0),
                                pitch: Cesium.Math.toRadians(-30.0),
                                roll: 0.0
                            }
                        });
                        update_viewer(map_viewer)
                        map_viewer.scene.requestRender();
                    } catch (e) {
                        console.error("Error setting camera view in Map:", e);
                    }
                }
            }
        }
    }, [map_viewer, selected_contact])

    return (
        <div className="map__container">
            <div
                className="cesium__container"
                ref={cesiumContainerRef}
            />
            {loading &&
                <div className="map__view">
                    <CustomLoader loading={loading} />
                </div>
            }
        </div >
    )
}

export default Map